/*@flow*/
import type { Element } from "react"; /** @jsx jsx */
import { css, jsx } from "@emotion/core";
import useHasFilters from "./useHasFilters";
import cx from "classnames";
import OverlayTriggerWrapper from "comps/OverlayTriggerWrapper";
import { Tooltip } from "react-bootstrap";
import isEmbedded from "lib/isEmbedded";
import useCommands from "./useCommands";
import useContextState from "./useContextState";
import {
  ltButtonBarColor,
  ltButtonBarBg,
} from "./colors";
import isSmallScreen from "lib/isSmallScreen";
import LTTutorial from "./LTTutorial";
import { useState, Fragment } from "react";

function useHasLayers() {
  const { layersActiveLayers } = useContextState();
  return layersActiveLayers.size > 0;
}

function LTButtonBarRawButton({
  iconClass,
  html,
  id,
  cssStyle,
  onClick,
  isActive,
  activePanel,
}: {
  iconClass: string,
  html: string,
  id: string,
  cssStyle?: string,
  onClick: () => any,
  isActive: boolean,
  activePanel: string,
}) {
  let opacity = 1;
  if (activePanel) {
    opacity = isActive ? 1 : 0.3;
  }

  return (
    <OverlayTriggerWrapper
      placement="right"
      trigger={["hover"]}
      rootClose
      overlay={
        <Tooltip id={"tooltip-" + id} className="left-toolbar-nav-link-popover">
          <div
            className="text-left"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </Tooltip>
      }
    >
      <button
        className="btn-block text-center p-3 btn btn-link"
        css={css(
          `
          height: 60px;
          color: ${ltButtonBarColor} !important;
          font-size: 1.8rem;
          border-right: ${isActive ? `1px solid #ccc` : "none"};
          opacity: ${opacity};
        `,
          cssStyle
        )}
        onClick={onClick}
        data-testid={"button-" + id}
        id={id}
      >
        <span className="position-relative">
          <i className={cx("fa", { [iconClass]: true })} />
        </span>
      </button>
    </OverlayTriggerWrapper>
  );
}

function LTButtonBarButton(props: {
  iconClass: string,
  html: string,
  id: string,
  cssStyle?: string,
}) {
  const cmd = useCommands();
  const { activePanel } = useContextState();
  // Compare activePanel to id of this to determine if this panel is active.
  const isActive = activePanel === props.id;

  const onClick = () => {
    cmd.togglePanel(props.id);
  };

  return (
    <LTButtonBarRawButton {...props} isActive={isActive} onClick={onClick} activePanel={activePanel} />
  );
}

function LTButtonBarLocation() {
  let iconClass = "fa-map-marker";
  const id = "LOCATION_DETAILS";
  const html =
    "<b>LOCATION DETAILS</b><p>Lookup details about records shown on the map.</p>";
  return <LTButtonBarButton id={id} html={html} iconClass={iconClass} />;
}

function LTButtonBarPublicData() {
  const iconClass = "fa-database";
  const id = "PUBLIC_DATA";
  const html =
    "<b>PUBLIC DATA</b><p>Look up available public data for this map view.</p>";
  return <LTButtonBarButton id={id} html={html} iconClass={iconClass} />;
}

function LTButtonBarFilters() {
  let iconClass = "fa-sliders-h";
  const hasFilters = useHasFilters();
  iconClass = hasFilters ? iconClass + " green-dot" : iconClass;
  const id = "FILTERS";
  const html =
    "<b>FILTERS</b><p>Sort through the records by setting up filters.</p>";
  return <LTButtonBarButton id={id} html={html} iconClass={iconClass} />;
}

function LTButtonBarLayers() {
  let iconClass = "fa-layer-group";
  const hasLayers = useHasLayers();
  iconClass = hasLayers ? iconClass + " green-dot" : iconClass;
  const html =
    "<div><b>MAP LAYERS</b><p>Turn on and off available map layers.</p></div>";
  const id = "LAYERS";
  return <LTButtonBarButton id={id} html={html} iconClass={iconClass} />;
}

function LTButtonBarAnalysis() {
  const iconClass = "fa-chart-bar";
  const id = "ANALYSIS";
  const html = "<b>ANALYZE</b><p>Analyze records in the current map view.</p>";
  return <LTButtonBarButton id={id} html={html} iconClass={iconClass} />;
}

function LTButtonBarTutorial() {
  const iconClass = "fa-question-circle";
  const id = "TUTORIAL";
  const html = "<b>TUTORIAL</b><p>Take a tour of the map.</p>";
  const { activePanel } = useContextState();
  const [isShowing, setShowing] = useState(false);
  // Toggle joyride
  const onClick = () => {
    setShowing((v) => !v);
  };
  if (!isEmbedded()) return null;

  return (
    <Fragment>
      <LTButtonBarRawButton
        isActive={false}
        id={id}
        html={html}
        iconClass={iconClass}
        onClick={onClick}
        activePanel={activePanel}
      />
      {isShowing && <LTTutorial onReset={() => setShowing(false)} />}
    </Fragment>
  );
}

function LTButtonBarTools() {
  const id = "TOOLS";
  const html =
    "<b>TOOLS</b><p>Modify your map settings & export local data.</p>";
  const iconClass = "fa-tools";
  if (isEmbedded()) return null;
  return <LTButtonBarButton id={id} html={html} iconClass={iconClass} />;
}

function LTButtonBarToggleMapView() {
  const id = "MAP VIEW";
  const html = "<b>MAP VIEW</b><p>Toggle map view</p>";
  const iconClass = "fa-map";
  const { activePanel } = useContextState();
  const isActive = Boolean(activePanel);
  const cmd = useCommands();
  if (!isEmbedded()) return null;
  if (!isSmallScreen()) return null;

  const onClick = () => {
    cmd.togglePanel("LOCATION_DETAILS");
  };

  return (
    <LTButtonBarRawButton
      id={id}
      html={html}
      iconClass={iconClass}
      onClick={onClick}
      isActive={!isActive}
    />
  );
}

const buttonMap = {
  LOCATION_DETAILS: LTButtonBarLocation,
  LIST_VIEW: LTButtonBarLocation,
  PUBLIC_DATA: LTButtonBarPublicData,
  FILTERS: LTButtonBarFilters,
  LAYERS: LTButtonBarLayers,
  ANALYSIS: LTButtonBarAnalysis,
  TUTORIAL: LTButtonBarTutorial,
  TOOLS: () => null,
};

function LTButtonBarFactory({ name }) {
  const Button = buttonMap[name];
  if (!Button) return null;
  return <Button />;
}

export function useButtonBarWidth(): number {
  return isSmallScreen() ? narrowButtonBarWidth : wideButtonBarWidth;
}

export const narrowButtonBarWidth = 45;
export const wideButtonBarWidth = 70;

export default function LTButtonBar(): Element<string> {
  const buttonBarWidth = useButtonBarWidth();

  const s = useContextState();

  return (
    <div
      data-testid="LTButtonBar"
      className="h-100"
      css={css(`
        width: ${buttonBarWidth}px;
        background: ${ltButtonBarBg};
        z-index: 999;
        position: relative;
      `)}
    >
      <div
        css={css(`
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          height: 100%;
        `)}
      >
        <div>
          {s.panels.map((name, i) => (
            <LTButtonBarFactory key={i} name={name.toUpperCase()} />
          ))}
        </div>
        <div>
          <LTButtonBarToggleMapView />
          <LTButtonBarTools />
        </div>
      </div>
    </div>
  );
}
